import { queryOptions, useQuery } from '@tanstack/react-query';
import { Loading } from 'corso-admin-ui';
import {
  CrewClaimResolutionMethodEnum,
  CrewClaimStatusCode,
  CrewClaimStatusDetailCode,
  crewClaimStatuses,
  CrewMerchantUi,
} from 'corso-types';
import { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import { z } from 'zod';
import api from '~/api';
import PageStatus from '~/components/PageStatus';
import { usePathParams } from '~/hooks/usePathParams';
import { useStoreId } from '~/providers/StoreUserProvider';

/**
 * Represents a resolution method that can be approved, as defined by those that can be finalized.
 * Has a redundant `typeof`; however, it's necessary as it provides assured keys for resulting union.
 */
export type SupportedResolvedResolutionMethod =
  CrewMerchantUi.ClaimFinalizePayload['claim']['claimLineItems']['approved'][keyof CrewMerchantUi.ClaimFinalizePayload['claim']['claimLineItems']['approved']][number]['resolutionMethodEnum'];

/**
 * Represents all supported and reviewable resolution methods mapped to themselves.
 * Useful to narrow types and only process reviewable resolution methods.
 * Additionally, if a resolution method is not actually supported as an `approveClaimLineItem`, this will error indicated which resolution method is missing/not supported in either the API or the UI.
 */
export const approveResolutionMethod = {
  [CrewClaimResolutionMethodEnum.refund]: CrewClaimResolutionMethodEnum.refund,
  [CrewClaimResolutionMethodEnum.giftCard]:
    CrewClaimResolutionMethodEnum.giftCard,
  [CrewClaimResolutionMethodEnum.variantExchange]:
    CrewClaimResolutionMethodEnum.variantExchange,
  [CrewClaimResolutionMethodEnum.replacementOrder]:
    CrewClaimResolutionMethodEnum.replacementOrder,
  [CrewClaimResolutionMethodEnum.other]: CrewClaimResolutionMethodEnum.other,
  [CrewClaimResolutionMethodEnum.repair]: CrewClaimResolutionMethodEnum.repair,
} as const satisfies {
  [ResolutionMethod in SupportedResolvedResolutionMethod]: ResolutionMethod;
};

/**
 * Given the original requested resolution, represents the resulting resolutions that are allowed from it.
 * Alike transitions for a state machine.
 */
// TODO rename to resolutionMethodTransform
export const resolutionMethodChangesAllowed = {
  [CrewClaimResolutionMethodEnum.refund]: [
    CrewClaimResolutionMethodEnum.refund, // self transition
    CrewClaimResolutionMethodEnum.giftCard,
    CrewClaimResolutionMethodEnum.variantExchange,
    CrewClaimResolutionMethodEnum.replacementOrder,
  ],
  [CrewClaimResolutionMethodEnum.giftCard]: [
    CrewClaimResolutionMethodEnum.refund,
    CrewClaimResolutionMethodEnum.giftCard, // self transition
    CrewClaimResolutionMethodEnum.variantExchange,
    CrewClaimResolutionMethodEnum.replacementOrder,
  ],
  [CrewClaimResolutionMethodEnum.variantExchange]: [
    CrewClaimResolutionMethodEnum.refund,
    CrewClaimResolutionMethodEnum.giftCard,
    CrewClaimResolutionMethodEnum.variantExchange, // self transition
    CrewClaimResolutionMethodEnum.replacementOrder,
  ],
  [CrewClaimResolutionMethodEnum.warrantyReview]: [
    CrewClaimResolutionMethodEnum.refund,
    CrewClaimResolutionMethodEnum.giftCard,
    CrewClaimResolutionMethodEnum.replacementOrder,
    CrewClaimResolutionMethodEnum.repair,
  ],
  [CrewClaimResolutionMethodEnum.other]: [
    // ! NOT INTENDED TO BE EDITED
    CrewClaimResolutionMethodEnum.other, // self transition
    // no other transitions, but deny/cancel allowed
  ],
  [CrewClaimResolutionMethodEnum.replacementOrder]: [
    CrewClaimResolutionMethodEnum.refund,
    CrewClaimResolutionMethodEnum.giftCard,
    CrewClaimResolutionMethodEnum.variantExchange,
    CrewClaimResolutionMethodEnum.replacementOrder, // self transition
  ],
  [CrewClaimResolutionMethodEnum.repair]: [
    CrewClaimResolutionMethodEnum.repair, // self transition
    CrewClaimResolutionMethodEnum.refund,
    CrewClaimResolutionMethodEnum.giftCard,
    CrewClaimResolutionMethodEnum.variantExchange,
    CrewClaimResolutionMethodEnum.replacementOrder,
  ],
} as const satisfies Partial<
  Record<
    CrewClaimResolutionMethodEnum,
    readonly CrewClaimResolutionMethodEnum[]
  >
>;

/**
 * Requested resolution method that has transitions to different resolutions.
 */
export type SupportedRequestedResolutionMethod =
  keyof typeof resolutionMethodChangesAllowed;

export const supportedRequestedResolutionMethod = {
  [CrewClaimResolutionMethodEnum.refund]: `${CrewClaimResolutionMethodEnum.refund}`,
  [CrewClaimResolutionMethodEnum.giftCard]: `${CrewClaimResolutionMethodEnum.giftCard}`,
  [CrewClaimResolutionMethodEnum.variantExchange]: `${CrewClaimResolutionMethodEnum.variantExchange}`,
  [CrewClaimResolutionMethodEnum.warrantyReview]: `${CrewClaimResolutionMethodEnum.warrantyReview}`,
  [CrewClaimResolutionMethodEnum.other]: `${CrewClaimResolutionMethodEnum.other}`,
  [CrewClaimResolutionMethodEnum.repair]: `${CrewClaimResolutionMethodEnum.repair}`,
  [CrewClaimResolutionMethodEnum.replacementOrder]: `${CrewClaimResolutionMethodEnum.replacementOrder}`,
} satisfies {
  [
    ResolutionMethod in SupportedRequestedResolutionMethod
  ]: `${ResolutionMethod}`;
};

/**
 * Resolution method that can be persisted through a transition from a requested resolution.
 */
export type SupportedModifiedResolutionMethod<
  T extends SupportedRequestedResolutionMethod =
    SupportedRequestedResolutionMethod,
> = (typeof resolutionMethodChangesAllowed)[T][number];

/**
 * Represents resolutions methods that are entirely unsupported by the UI.
 * These resolution methods cannot be reviewed or resolved by a transition from a requested resolution.
 * Expected to always be `never`.
 */
// ! basically expected to always be `never`
export type UnsupportedResolutionMethod = Exclude<
  SupportedResolvedResolutionMethod,
  SupportedModifiedResolutionMethod | SupportedRequestedResolutionMethod
>;

/** Determines if a claim line item can be changed to another resolution method from the requested resolution. */
export function canChangeResolution(
  claimLineItem: CrewMerchantUi.ClaimLineItem,
): claimLineItem is SupportedClaimLineItem {
  return (
    claimLineItem.requestedResolutionMethodEnum in
    resolutionMethodChangesAllowed
  );
}

export const reviewableResolutionMethods = Object.values(
  CrewClaimResolutionMethodEnum,
).filter(
  (resolutionMethod): resolutionMethod is SupportedRequestedResolutionMethod =>
    resolutionMethod in resolutionMethodChangesAllowed,
);

/**
 * Resolution methods which can be the result of a review.
 * The result of the transitions available.
 * Can provide a specific reviewable resolution method to get the resolvable resolution methods for it.
 */
export type TransformableResolutionMethod =
  (typeof resolutionMethodChangesAllowed)[SupportedRequestedResolutionMethod][number];

export function isResolvableResolutionMethod(
  resolutionMethod: CrewClaimResolutionMethodEnum,
): resolutionMethod is SupportedResolvedResolutionMethod {
  return resolutionMethod in approveResolutionMethod;
}

export type SupportedResolutionMethod =
  | SupportedRequestedResolutionMethod
  | SupportedResolvedResolutionMethod;

export const supportedResolutionMethods = Object.values(
  CrewClaimResolutionMethodEnum,
).filter(
  (resolutionMethod): resolutionMethod is SupportedResolutionMethod =>
    resolutionMethod in resolutionMethodChangesAllowed ||
    resolutionMethod in approveResolutionMethod,
);

export const unsupportedResolutionMethods = Object.values(
  CrewClaimResolutionMethodEnum,
).filter(
  (resolutionMethod): resolutionMethod is UnsupportedResolutionMethod =>
    !(resolutionMethod in resolutionMethodChangesAllowed),
);

export type SupportedClaimLineItem<
  SupportedResolution extends SupportedRequestedResolutionMethod =
    SupportedRequestedResolutionMethod,
> = {
  [R in SupportedResolution]: CrewMerchantUi.ClaimLineItem & {
    requestedResolutionMethodEnum: `${R}`;
  };
}[SupportedResolution];

export function isSupportedClaimLineItem<
  R extends SupportedRequestedResolutionMethod =
    SupportedRequestedResolutionMethod,
>(
  claimLineItem: CrewMerchantUi.ClaimLineItem,
  requestedResolutionMethod?: R,
): claimLineItem is SupportedClaimLineItem<R> {
  if (requestedResolutionMethod) {
    return (
      claimLineItem.requestedResolutionMethodEnum === requestedResolutionMethod
    );
  }

  return (
    claimLineItem.requestedResolutionMethodEnum in
    supportedRequestedResolutionMethod
  );
}

/** Equivalent to `isSupportedClaimLineItem`, but has a required requested resolution method. */
export function isRequestedAs<R extends SupportedRequestedResolutionMethod>(
  claimLineItem: CrewMerchantUi.ClaimLineItem,
  requestedResolutionMethod: R,
) {
  return isSupportedClaimLineItem(claimLineItem, requestedResolutionMethod);
}

export function isRequestedOther(claimLineItem: CrewMerchantUi.ClaimLineItem) {
  return isRequestedAs(claimLineItem, CrewClaimResolutionMethodEnum.other);
}

/**
 * Represent if `claim` or `claimLineItem` is locked and can no longer be edited; i.e. is in a terminal state.
 * Can optionally specify the `claim` or `claimLineItem` or both.
 * Depending on what is provided, it checks if the `claimLineItem` is locked individually, or compares against the whole `claim`.
 * When `true`, either the line item or the whole claim is locked, and should not be edited.
 */
export function isLocked({
  claim,
  claimLineItem,
}:
  | {
      claim: ClaimReview;
      claimLineItem?: SupportedClaimLineItem;
    }
  | {
      claim?: ClaimReview;
      claimLineItem: SupportedClaimLineItem;
    }) {
  // ! special case: currently an "Other" line item is considered locked and cannot be edited
  if (claimLineItem && isRequestedOther(claimLineItem)) return true;

  const zeroedThroughDeduction =
    claim?.requestSummary?.approvedLineItems.find(
      (lineItem) => lineItem.id === claimLineItem?.id,
    )?.wasZeroedThroughDeduction ?? false;

  // gets set when claim is approved/denied
  const isFinalized =
    !!claimLineItem?.finalizedOn ||
    !!claim?.finalizedOn ||
    claim?.claimLineItems.every(({ finalizedOn }) => !!finalizedOn);

  // ? I think this could be simplified to not even look at the statuses on the line items.
  // is any marked as canceled, the whole claim should've been marked as canceled for what remained
  const isCanceled =
    claimLineItem?.status.code === CrewClaimStatusCode.closed ||
    claim?.claimLineItems.every(
      ({ status, canceledOn }) =>
        status.code === CrewClaimStatusCode.closed || !!canceledOn,
    );

  return isFinalized || isCanceled || zeroedThroughDeduction;
}

export type ResolvedApprovedClaimLineItem<
  Approval extends SupportedResolvedResolutionMethod =
    SupportedResolvedResolutionMethod,
> = SupportedClaimLineItem & {
  resolutionMethodEnum: `${Approval}`;
};

export function isResolvedApproval(
  claimLineItem: SupportedClaimLineItem,
): claimLineItem is ResolvedApprovedClaimLineItem {
  return (
    !isLocked({ claimLineItem }) &&
    claimLineItem.resolutionMethodEnum !== undefined &&
    claimLineItem.resolutionMethodEnum in approveResolutionMethod
  );
}

export function isResolvedApprovalAs<
  R extends SupportedResolvedResolutionMethod,
>(
  claimLineItem: SupportedClaimLineItem,
  resolutionMethod: R,
): claimLineItem is ResolvedApprovedClaimLineItem<R> {
  return (
    isResolvedApproval(claimLineItem) &&
    claimLineItem.resolutionMethodEnum === resolutionMethod
  );
}

const denialDetailCodes = crewClaimStatuses[
  CrewClaimStatusCode.denied
].claimStatusDetail.map((detail) => detail.code);
type DenialDetailCode = (typeof denialDetailCodes)[number];

export type DeniedClaimLineItem = SupportedClaimLineItem & {
  status: {
    code: typeof CrewClaimStatusCode.denied;
    detail: { code: `${DenialDetailCode}` };
  };
};

export function isDenial(
  claimLineItem: SupportedClaimLineItem,
): claimLineItem is DeniedClaimLineItem {
  if (claimLineItem.deniedOn) return true;
  return (
    !isLocked({ claimLineItem }) &&
    claimLineItem.status.code === CrewClaimStatusCode.denied &&
    // ! type cast to broaden the underlying type // TODO consider alternative ways to represent this
    (denialDetailCodes as `${CrewClaimStatusDetailCode}`[]).includes(
      claimLineItem.status.detail.code,
    )
  );
}

export function isDenialAs<R extends SupportedRequestedResolutionMethod>(
  claimLineItem: SupportedClaimLineItem,
  requestedResolutionMethod: R,
): claimLineItem is SupportedClaimLineItem<R> & DeniedClaimLineItem {
  return (
    isDenial(claimLineItem) &&
    isRequestedAs(claimLineItem, requestedResolutionMethod)
  );
}

/**
 * Represents a claim line item that is not in a valid approved or denied state.
 */
// * notably this cannot be a predicate that just verifies the claim line item does not have a resolution method set
export function isUndecided(claimLineItem: SupportedClaimLineItem) {
  return (
    !isLocked({ claimLineItem }) &&
    !isResolvedApproval(claimLineItem) &&
    !isDenial(claimLineItem)
  );
}

type DefaultApproveResolutionMethod = SupportedResolvedResolutionMethod &
  SupportedRequestedResolutionMethod;
export type DefaultApprovedClaimLineItem<
  Approval extends DefaultApproveResolutionMethod =
    DefaultApproveResolutionMethod,
> = SupportedClaimLineItem<Approval> & {
  resolutionMethodEnum?: undefined;
};

export function isDefaultApproval(
  claimLineItem: SupportedClaimLineItem,
): claimLineItem is DefaultApprovedClaimLineItem {
  return (
    !isLocked({ claimLineItem }) &&
    (isUndecided(claimLineItem) ||
      claimLineItem.resolutionMethodEnum ===
        claimLineItem.requestedResolutionMethodEnum)
  );
}

export function isDefaultApprovalAs<R extends DefaultApproveResolutionMethod>(
  claimLineItem: SupportedClaimLineItem,
  resolutionMethod: R,
): claimLineItem is DefaultApprovedClaimLineItem<R> {
  return (
    isDefaultApproval(claimLineItem) &&
    claimLineItem.requestedResolutionMethodEnum === resolutionMethod
  );
}

export type DefaultOrResolvedApprovalLineItem<
  R extends DefaultApproveResolutionMethod,
> = DefaultApprovedClaimLineItem<R> | ResolvedApprovedClaimLineItem<R>;

export function isDefaultOrResolvedApproval(
  claimLineItem: SupportedClaimLineItem,
) {
  return isDefaultApproval(claimLineItem) || isResolvedApproval(claimLineItem);
}

export function isDefaultOrResolvedApprovalAs<
  R extends DefaultApproveResolutionMethod,
>(
  claimLineItem: SupportedClaimLineItem,
  resolutionMethod: R,
): claimLineItem is DefaultOrResolvedApprovalLineItem<R> {
  return (
    isDefaultApprovalAs(claimLineItem, resolutionMethod) ||
    isResolvedApprovalAs(claimLineItem, resolutionMethod)
  );
}

export type ClaimReview = Omit<CrewMerchantUi.Claim, 'claimLineItems'> & {
  claimLineItems: SupportedClaimLineItem[];
  refetch: () => Promise<void>;
};

/** A claim's query, shared with anything else showing the claim the page has loaded (e.g. Sidekick). */
export const claimQueryOptions = (
  storeId: ReturnType<typeof useStoreId>,
  claimId: number | undefined,
) =>
  queryOptions({
    queryKey: ['claim', claimId, storeId],
    queryFn: () => api.store(storeId).claim(`${claimId}`).get(),
  });

/**
 * The query for the current claim.
 */
function useClaimQuery() {
  const { claimId } = usePathParams(
    z.object({ claimId: z.coerce.number().optional() }),
  );
  const storeId = useStoreId();

  return useQuery({
    ...claimQueryOptions(storeId, claimId),
    enabled: !!claimId,
    throwOnError: false,
    select: (claim) => {
      const { claimLineItems } = claim;
      claimLineItems.sort((a, b) => a.id - b.id); // * sort line items by id for consistency order on updates
      return {
        ...claim,
        claimLineItems: claimLineItems.filter((claimLineItem) =>
          isSupportedClaimLineItem(claimLineItem),
        ),
      };
    },
  });
}

const ClaimContext = createContext<ClaimReview | null>(null);

/**
 * The currently loaded claim as available through the provider.
 */
export function useClaim() {
  const claim = useContext(ClaimContext);
  if (!claim) {
    throw new Error('useClaim must be used within a ClaimProvider');
  }

  return claim;
}

/**
 * Wrapper around `isLocked` as a hook that uses the current claim from the context.
 * This is useful for components that need to determine if the claim or a specific line item is locked.
 * If no line item is provided, it checks the whole claim.
 */
export function useIsLocked(claimLineItem?: SupportedClaimLineItem) {
  const claim = useClaim();

  return isLocked({ claim, claimLineItem });
}

/** Only intended within `ClaimReview`. */
export function ClaimReviewProvider({ children }: PropsWithChildren) {
  const { data: claim, error, isError, isPending, refetch } = useClaimQuery();

  const value = useMemo<ClaimReview | null>(() => {
    if (!claim) return null;
    return {
      ...claim,
      refetch: async () => {
        await refetch();
      },
    };
  }, [claim, refetch]);

  if (isError) {
    // TODO differentiate client and server errors, and only propagate server/request errors
    return <PageStatus.Error error={error} />;
  }

  if (isPending) {
    return (
      <>
        <Loading />
        <PageStatus.Loading />
      </>
    );
  }

  return (
    <ClaimContext.Provider value={value}>{children}</ClaimContext.Provider>
  );
}
